import type { ReactNode } from 'react';
import { AuthField, AuthFrame as Frame, type AuthFrameProps } from '@forecastify/ui';
import logo from '@forecastify/ui/assets/forecastify-logo-white.png';
import { useT } from '../../i18n';
import { AuthBackdrop } from './auth-backdrop';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ THE EIGHT CHROMELESS SCREENS SHARE THIS FRAME. This file is the adapter.   │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The frame itself moved to `packages/ui` in S3, when the Control Panel needed the same card
 * (`decisions.md` S-6). What is left here is the three things that are **this application's** and
 * not the other's: its wordmark, its animated backdrop, and its own footer strings.
 *
 * ⚠️ **Every screen imports this, unchanged.** Sign-in, forgot-password, reset-password, the
 * invitation, `/sign-up`, `/auth/verify-email`, `/welcome` and S3's `/suspended` — eight, and
 * `/welcome` and `/suspended` are signed **in**. The honest description of what the frame draws is
 * *the page with no workspace behind it*, which is why `AppShell` is absent from all eight.
 *
 * ⚠️ **`AuthField` is re-exported rather than re-wrapped**, because it has nothing app-local in it.
 * A pass-through component would be a second place for its geometry to drift from the mockup's.
 */
export { AuthField };

export function AuthFrame(
  props: Omit<AuthFrameProps, 'logo' | 'backdrop' | 'pageFooter'>,
): ReactNode {
  const t = useT();

  return (
    <Frame
      {...props}
      /**
       * ⚠️⚠️ **THE WHITE WORDMARK, AND IT IS LEGIBLE ONLY BECAUSE OF THE BACKDROP.** The dark one
       * shipped first, against the near-white page the mockup draws — and the moment the waves went
       * in behind it, the colour it needed inverted. Both files are kept in `packages/ui/src/assets`:
       * they are the same artwork, the coloured mark to the left of the wordmark is identical in
       * each, and the dark one is the right file the instant a page stops having a coloured
       * backdrop. **The Control Panel uses that one**, for exactly this reason.
       *
       * ⚠️ **It follows the BACKDROP, not the theme.** This is not a dark-mode question and
       * `applyTheme` has nothing to do with it.
       */
      logo={{ src: logo, alt: t('app.name') }}
      backdrop={<AuthBackdrop />}
      pageFooter={
        /**
         * ⚠️ **The mockup's footer, minus its version number.** It draws `v3.4.0` in mono beside the
         * links — a prototype's build string, and we have no version to put there: nothing in this
         * repository stamps one into the client, and hardcoding `v3.4.0` would be a fabricated fact
         * on the page, which is the kind of placeholder that reads as true.
         *
         * ⚠️ **`Adatkezelés` and `Súgó` have no destinations yet**, so they are text rather than
         * dead links — an `<a href="#">` that goes nowhere is the "visible control that does
         * nothing" failure this project refuses everywhere else.
         *
         * ⚠️⚠️ **WHITE, WITH NO SCRIM, AND THE CONTRAST HERE IS THE PRODUCT OWNER'S CALL.**
         * Measured under the glyph rectangles across eight frames at the foot of the page, white is
         * **3.18:1** where WCAG AA wants 4.5:1 for 12.5px text. It is legible — the band behind it
         * is the most saturated part of the gradient — but it does not meet the bar, and the three
         * things it names are a company name and two links that go nowhere yet. Recorded rather than
         * silently "fixed": the alternatives were a scrim (removed on request) or near-black type,
         * and neither is what this page is meant to look like.
         */
        <p className="absolute inset-x-0 bottom-0 z-10 flex items-center justify-center gap-3.5 p-6 text-[12.5px] text-white">
          <span>{t('auth.footer.company')}</span>
          <span>{t('auth.footer.privacy')}</span>
          <span>{t('auth.footer.help')}</span>
        </p>
      }
    />
  );
}
